import React from 'react';
import { useAppSelector } from '../../redux/hooks';
import { useComponentList } from '../../hooks/useComponentList';
import { useComponentSpecifications } from '../../hooks/useComponentSpecifications';
import SiteSelector from '../SiteSelector';
import SearchableDropdown from '../componentSpecs/SearchableDropdown';
import ComponentTypeGroup from '../componentSpecs/ComponentTypeGroup';
import { formatText } from '../componentSpecs/helpers';
import { Search, Database } from 'lucide-react';

const ComponentSpecificationsTab2: React.FC = () => {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  // Use optimized component list hook (single request instead of paginated entities + connections)
  // exclude_modules=false to include PV modules in the component list
  const { entities, isLoading: isLoadingEntities } = useComponentList(false);

  const {
    searchTerm,
    setSearchTerm,
    expandedTypes,
    typeSearchTerms,
    selectedModel,
    selectedCategory,
    allModels,
    allCategories,
    filteredEntities,
    entitiesByType,
    sortedTypes,
    toggleType,
    updateTypeSearch,
    selectModel,
    selectCategory,
  } = useComponentSpecifications(entities);

  // Show loading state
  if (isLoadingEntities) {
    return (
      <div className="eg-page-shell flex items-center justify-center eg-text-primary">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-engramiq-green border-t-transparent rounded-full animate-spin mb-4"></div>
          <div>Loading components...</div>
        </div>
      </div>
    );
  }

  // Show "No Site Selected" only if no site is selected in Redux
  if (!currentSite) {
    return (
      <div className="eg-page-shell flex flex-col items-center justify-center eg-text-primary p-6">
        <div className="flex flex-col items-center mb-6">
          <Database size={48} className="mb-4 opacity-50" />
          <div className="text-xl font-semibold mb-2">No Site Selected</div>
          <p className="mt-2 text-sm opacity-75 mb-4">Please select a site to view components.</p>
        </div>
        <div className="w-full max-w-md">
          <SiteSelector />
        </div>
      </div>
    );
  }

  // Show empty state if site is selected but no entities loaded yet (and not loading)
  if (entities.length === 0 && !isLoadingEntities) {
    return (
      <div className="eg-page-shell flex flex-col items-center justify-center eg-text-primary p-6">
        <div className="flex flex-col items-center mb-6">
          <Database size={48} className="mb-4 opacity-50" />
          <div className="text-xl font-semibold mb-2">No Components Available</div>
          <p className="mt-2 text-sm opacity-75 mb-4">This site has no components to display.</p>
        </div>
        <div className="w-full max-w-md">
          <SiteSelector />
        </div>
      </div>
    );
  }

  return (
    <div className="eg-page-shell flex flex-col">
      {/* Header */}
      <div className="eg-toolbar-header flex-shrink-0 px-6 pt-5 pb-4">
        {/* Site Selector */}
        <div className="mb-4">
          <SiteSelector />
        </div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold eg-text-primary">Component List</h2>
        </div>
        <div className="text-sm eg-text-secondary mb-4">
          {filteredEntities.length} component{filteredEntities.length !== 1 ? 's' : ''} available
        </div>

        {/* Page-wide Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 eg-text-secondary" />
          <input
            type="text"
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg eg-text-primary placeholder-[var(--color-text-secondary)] focus:outline-none focus:border-engramiq-green focus:ring-1 focus:ring-engramiq-green transition-all"
          />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 gap-3">
          <SearchableDropdown
            label="Model"
            value={selectedModel}
            allLabel="All Models"
            searchPlaceholder="Filter models..."
            options={allModels}
            formatOption={(m) => m.toUpperCase()}
            onSelect={selectModel}
          />
          <SearchableDropdown
            label="Category"
            value={selectedCategory}
            allLabel="All Categories"
            searchPlaceholder="Filter categories..."
            options={allCategories}
            formatOption={formatText}
            onSelect={selectCategory}
          />
        </div>
      </div>

      {/* Content */}
      <div className="eg-page-content flex-1 overflow-y-auto">
        <div className="px-6 py-5">
        {sortedTypes.length === 0 ? (
          <div className="text-center py-8">
            <Database className="w-12 h-12 eg-text-secondary mx-auto mb-4" />
            <p className="eg-text-secondary">No components found in this extraction.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedTypes.map(type => (
              <ComponentTypeGroup
                key={type}
                type={type}
                typeEntities={entitiesByType[type]}
                isExpanded={expandedTypes.has(type)}
                typeSearch={typeSearchTerms[type] || ''}
                onToggle={() => toggleType(type)}
                onSearchChange={(value) => updateTypeSearch(type, value)}
              />
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ComponentSpecificationsTab2;
